import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_TAGS } from '@/shared/api';
import { downloadFileFromBlob } from '@/shared/lib/utils/file';
import {
  deleteDownloadTemplate,
  downloadTemplateFile,
  fetchDownloadTemplates,
  fetchDownloadTemplateVariables,
  previewTemplate,
  uploadDownloadTemplate,
} from './api';
import type { DownloadTemplateEntityType } from './types';

/** Query keys шаблонов скачиваемых документов */
export const downloadTemplateKeys = {
  all: [API_TAGS.DOWNLOAD_TEMPLATES] as const,
  list: (entityType: DownloadTemplateEntityType) => [...downloadTemplateKeys.all, 'list', entityType] as const,
  variables: (entityType: DownloadTemplateEntityType) =>
    [...downloadTemplateKeys.all, 'variables', entityType] as const,
};

/** Хук перечня шаблонов сущности: им же наполняется список выбора у кнопок скачивания. */
export const useDownloadTemplates = (entityType: DownloadTemplateEntityType) =>
  useQuery({
    queryKey: downloadTemplateKeys.list(entityType),
    queryFn: () => fetchDownloadTemplates(entityType),
  });

/** Хук справки о переменных сущности: перечень ведёт сам код подстановки на бэкенде. */
export const useDownloadTemplateVariables = (entityType: DownloadTemplateEntityType) =>
  useQuery({
    queryKey: downloadTemplateKeys.variables(entityType),
    queryFn: () => fetchDownloadTemplateVariables(entityType),
  });

/** Хук загрузки шаблона: по успеху перечень своей сущности перезапрашивается. */
export const useUploadDownloadTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: uploadDownloadTemplate,
    onSuccess: (_template, { entityType }) =>
      queryClient.invalidateQueries({ queryKey: downloadTemplateKeys.list(entityType) }),
  });
};

/**
 * Хук скачивания файла шаблона: файл сохраняется на диск под тем именем, под которым был
 * загружен, — заголовок ответа кросс-доменному запросу браузером не открывается.
 */
export const useDownloadTemplateFile = () =>
  useMutation({
    mutationFn: ({ id }: { id: number; fileName: string }) => downloadTemplateFile(id),
    onSuccess: (blob, { fileName }) => {
      downloadFileFromBlob(blob, fileName);
    },
  });

/** Хук удаления шаблона: по успеху перечень своей сущности перезапрашивается. */
export const useDeleteDownloadTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }: { id: number; entityType: DownloadTemplateEntityType }) => deleteDownloadTemplate(id),
    onSuccess: (_result, { entityType }) =>
      queryClient.invalidateQueries({ queryKey: downloadTemplateKeys.list(entityType) }),
  });
};

/**
 * Хук проверки шаблона на записи: результат подстановки диалога в присланный текст шаблона.
 * Проверка ничего не сохраняет, поэтому и кеш ею не обесценивается.
 */
export const useTemplatePreview = () =>
  useMutation({
    mutationFn: previewTemplate,
  });
